import { Context } from "Context";
import { useCallback, useContext } from "react";
import { trackLink } from "utils/analyticsFramework";
import {
	buildDaConversationId,
	daAnalyticsStore,
	isDiscoveryAgentLink,
} from "utils/discoveryAgent";
import appConfig from "config.json";

let clickLocked = false;

export const resetClickLock = () => {
	clickLocked = false;
};

const AnalyticsWrapper = ({
	analyticsLinkId,
	children,
	pageTransitionLink = true,
	link = null,
}) => {
	const {
		config: { brand },
	} = useContext(Context);

	// Discovery Agent only applies to brands configured for it (e.g. WDW).
	// Brands without it (DVC, DLR, ...) skip the per-link DA check entirely.
	const discoveryAgentEnabled =
		!!appConfig.brands[brand]?.discoveryAgentEnabled;

	const trackAnalyticsWrapperLink = useCallback(
		(userInteraction = "HOVER") => {
			const linkId = `${analyticsLinkId}_${userInteraction}`;
			const sendsPageTransitionLink =
				appConfig.sendPageTransitionLinkBrands.includes(brand);

			// Discovery Agent entry points carry extra click context (store /
			// conversationId) and use the dual-fire pattern: a standalone link
			// call with the context, then a separate page-transition link that
			// forwards only the linkId to the next page view. Only evaluated when
			// the brand has Discovery Agent enabled, so brands without DA (e.g. DVC,
			// DLR) skip the per-link check entirely. Regular links are untouched.
			if (discoveryAgentEnabled && isDiscoveryAgentLink(link)) {
				const daProps = { store: daAnalyticsStore };
				const conversationId = buildDaConversationId();
				if (conversationId) daProps.conversationId = conversationId;

				trackLink({ linkId, ...daProps });
				if (sendsPageTransitionLink) {
					trackLink({ linkId, pageTransitionLink });
				}
				return;
			}

			const trackLinkData = { linkId };
			if (sendsPageTransitionLink) {
				trackLinkData.pageTransitionLink = pageTransitionLink;
			}
			trackLink(trackLinkData);
		},
		[analyticsLinkId, brand, pageTransitionLink, link, discoveryAgentEnabled]
	);

	return (
		<span
			name={analyticsLinkId ? `&lid=${analyticsLinkId}` : null}
			tabIndex={-1}
			style={{ display: "contents" }}
			onClick={() => {
				if (analyticsLinkId) {
					if (pageTransitionLink) clickLocked = true;
					trackAnalyticsWrapperLink("CLICK");
				}
			}}
			onMouseEnter={() => {
				if (analyticsLinkId && !clickLocked) {
					trackAnalyticsWrapperLink();
				}
			}}
			className="syndicated-analytics-wrapper"
		>
			{children}
		</span>
	);
};

export default AnalyticsWrapper;
